<template>
  <!-- Compact: one text line with an inline link-style action (inside cards). -->
  <div v-if="compact" :class="['flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs', toneTextClass]">
    <Icon :name="icon" class="h-3 w-3 flex-none" />
    <span>{{ text }}</span>
    <button
      type="button"
      class="font-semibold text-primary underline-offset-4 hover:underline"
      @click="openProGate(feature)"
    >
      {{ ctaLabel }}
    </button>
  </div>

  <div
    v-else
    :class="[
      'flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-md border px-3.5 py-3 text-[13.5px] leading-normal',
      toneBoxClass,
    ]"
  >
    <Icon :name="icon" :class="['h-[18px] w-[18px] flex-none', isGuest ? 'text-primary' : '']" />
    <div class="min-w-[180px] flex-1">
      <p v-if="title && !isGuest" class="font-medium">{{ title }}</p>
      <p :class="title && !isGuest ? 'mt-0.5' : ''">{{ text }}</p>
    </div>
    <Button type="button" size="sm" class="ms-auto flex-none gap-1.5" @click="openProGate(feature)">
      <Icon :name="isGuest ? 'lucide:log-in' : 'lucide:zap'" class="h-3.5 w-3.5" />
      <span>{{ ctaLabel }}</span>
    </Button>
  </div>
</template>

<script setup lang="ts">
// Pro gate notice with a call to action that matches the viewer's state:
// guests sign in, free users start the trial or upgrade. Every render counts
// as a gate view and every press as a gate click (useUpsellTracking).
const props = withDefaults(
  defineProps<{
    /** Feature key for funnel tracking, e.g. "ai_translate". */
    feature: string;
    /** Optional bold first line for signed-in users. */
    title?: string;
    /** Overrides the default copy for signed-in users. Guests always see the sign-in prompt. */
    message?: string;
    /** Show `message` to guests too (limits that matter before signing in). */
    messageForGuests?: boolean;
    tone?: "destructive" | "warning";
    compact?: boolean;
  }>(),
  { title: "", message: "", messageForGuests: false, tone: "destructive", compact: false }
);

const { proGateHint, gateState, ctaLabel, openProGate } = useProGateHint();
const { trackUpsell } = useUpsellTracking();

const isGuest = computed(() => gateState.value === "guest");
const text = computed(() =>
  props.message && (!isGuest.value || props.messageForGuests)
    ? props.message
    : proGateHint.value
);
const icon = computed(() => (isGuest.value ? "lucide:user-round" : "lucide:lock"));

const toneTextClass = computed(() =>
  props.tone === "warning" ? "text-warning" : "text-destructive"
);
// Status Tint Rule: status color text on a 10% tint with a ~25% border.
// Guests get the neutral info tint since signing in is not an error.
const toneBoxClass = computed(() => {
  if (isGuest.value) return "border-info/25 bg-info/10";
  return props.tone === "warning"
    ? "border-warning/25 bg-warning/10 text-warning"
    : "border-destructive/25 bg-destructive/10 text-destructive";
});

watch(
  gateState,
  (gate) => trackUpsell("gate_view", { feature: props.feature, gate }),
  { immediate: true }
);
</script>
